import { useCallback, useRef, useState } from 'react';
import { format } from 'date-fns';
import { apiClient } from 'c-data';
import { ReportDateTimeframe } from 'c-reports/Types';
import type { ElasticSearchSummaryResponse } from 'c-sdk';
import { callApi } from 'c-data-layer';

type LoadParams = {
    start: Date;
    end: Date;
    timeframe: ReportDateTimeframe;
};

type Params = {
    id: number;
    isDsp: boolean;
    hideImpressionAnalysis: boolean;
    isNonAuditedAudienceOnly: boolean;
    environmentMode: string | null | undefined;
    impactAnalysisFieldFilters: Record<string, string[]>;
    nonAuditedTotalFieldFilters: Record<string, string[]>;
    source: string;
    dispatch: any;
    urlDateFormat: string;
};

export const useCampaignAnalyticsCompanionSummary = ({
    id,
    isDsp,
    hideImpressionAnalysis,
    isNonAuditedAudienceOnly,
    environmentMode,
    impactAnalysisFieldFilters,
    nonAuditedTotalFieldFilters,
    source,
    dispatch,
    urlDateFormat,
}: Params) => {
    const [auditedPcaData, setAuditedPcaData] = useState<ElasticSearchSummaryResponse>(null);
    const [nonAuditedTotalPcaData, setNonAuditedTotalPcaData] =
        useState<ElasticSearchSummaryResponse>(null);
    const [impactAnalysisLoading, setImpactAnalysisLoading] = useState(false);

    const impactAnalysisSummaryRequestRef = useRef<AbortController | null>(null);
    const latestImpactAnalysisRequestKeyRef = useRef<string | null>(null);
    const nonAuditedTotalSummaryRequestRef = useRef<AbortController | null>(null);
    const latestNonAuditedTotalRequestKeyRef = useRef<string | null>(null);

    const loadCompanionSummaries = useCallback(
        async ({ start, end, timeframe }: LoadParams) => {
            if (!isDsp) {
                setAuditedPcaData(null);
                setNonAuditedTotalPcaData(null);
                setImpactAnalysisLoading(false);
                return;
            }

            if (isNonAuditedAudienceOnly) {
                latestImpactAnalysisRequestKeyRef.current = null;
                const nonAuditedPayload = {
                    campaignId: id,
                    startDate:
                        timeframe === ReportDateTimeframe.All
                            ? undefined
                            : format(start, urlDateFormat),
                    endDate:
                        timeframe === ReportDateTimeframe.All
                            ? undefined
                            : format(end, urlDateFormat),
                    environment: environmentMode,
                    fieldFilters: nonAuditedTotalFieldFilters,
                    showStats: true,
                    source,
                };
                const nonAuditedRequestKey = JSON.stringify(nonAuditedPayload);

                if (latestNonAuditedTotalRequestKeyRef.current !== nonAuditedRequestKey) {
                    latestNonAuditedTotalRequestKeyRef.current = nonAuditedRequestKey;
                    nonAuditedTotalSummaryRequestRef.current?.abort();
                    const requestData = apiClient.ReportsElasticSearch.summary(nonAuditedPayload);
                    nonAuditedTotalSummaryRequestRef.current = requestData.controller;
                    const [nonAuditedErr, , nonAuditedResponse] = await dispatch(
                        callApi(requestData.request),
                    );
                    setNonAuditedTotalPcaData(
                        !nonAuditedErr && nonAuditedResponse?.data ? nonAuditedResponse.data : null,
                    );
                }

                setAuditedPcaData(null);
                setImpactAnalysisLoading(false);
                return;
            }

            if (hideImpressionAnalysis) {
                latestImpactAnalysisRequestKeyRef.current = null;
                latestNonAuditedTotalRequestKeyRef.current = null;
                impactAnalysisSummaryRequestRef.current?.abort();
                nonAuditedTotalSummaryRequestRef.current?.abort();
                setAuditedPcaData(null);
                setNonAuditedTotalPcaData(null);
                setImpactAnalysisLoading(false);
                return;
            }

            setNonAuditedTotalPcaData(null);
            latestNonAuditedTotalRequestKeyRef.current = null;

            const payload = {
                campaignId: id,
                startDate:
                    timeframe === ReportDateTimeframe.All
                        ? undefined
                        : format(start, urlDateFormat),
                endDate:
                    timeframe === ReportDateTimeframe.All ? undefined : format(end, urlDateFormat),
                environment: environmentMode,
                fieldFilters: impactAnalysisFieldFilters,
                showStats: true,
                source,
            };
            const impactAnalysisRequestKey = JSON.stringify(payload);

            if (latestImpactAnalysisRequestKeyRef.current === impactAnalysisRequestKey) {
                setImpactAnalysisLoading(false);
                return;
            }

            latestImpactAnalysisRequestKeyRef.current = impactAnalysisRequestKey;

            try {
                setImpactAnalysisLoading(true);
                impactAnalysisSummaryRequestRef.current?.abort();
                const requestData = apiClient.ReportsElasticSearch.summary(payload);
                impactAnalysisSummaryRequestRef.current = requestData.controller;
                const [audErr, , aud] = await dispatch(callApi(requestData.request));
                setAuditedPcaData(!audErr && aud?.data ? aud.data : null);
            } finally {
                setImpactAnalysisLoading(false);
            }
        },
        [
            dispatch,
            environmentMode,
            hideImpressionAnalysis,
            id,
            impactAnalysisFieldFilters,
            isNonAuditedAudienceOnly,
            isDsp,
            nonAuditedTotalFieldFilters,
            source,
            urlDateFormat,
        ],
    );

    return {
        auditedPcaData,
        nonAuditedTotalPcaData,
        impactAnalysisLoading,
        setImpactAnalysisLoading,
        loadCompanionSummaries,
    };
};
